import { useMemo } from 'react'
import {
  filterByDateRange,
  filterBySelectedIds,
  sortCertificates,
  type SortDescriptor
} from 'utils/certificateUtils'
import { STATUS_FAMILY_MAP } from 'constants/certificateConstants'
import type { CerteficateTab, Sort } from 'types'

export interface CertificateFilters {
  limit: number
  offset: number
  tab: CerteficateTab
  status: string | null
  type: string | null
  sort: Sort | null
  sort_by: string | null
  query: string | null
  certificateIds: string | null
  activityFrom?: string
  activityTo?: string
  /** VERA-315: multi-select status-family filter (StatusPillFilter). Empty/undefined = no filtering. */
  statuses?: string[]
}


export interface UseCertificateFiltersProps {
  certificates: any[] | undefined
  filters: CertificateFilters
  additionalSorts: Array<{ field: string; direction: 'ASC' | 'DESC' }>
  tab: CerteficateTab
}

export const useCertificateFilters = ({
  certificates,
  filters,
  additionalSorts,
  tab,
}: UseCertificateFiltersProps) => {
  const baseFilteredCertificates = useMemo(() => {
    if (!certificates) return []
    
    let filtered = certificates
    
    filtered = filterBySelectedIds(filtered, filters.certificateIds || undefined)
    
    filtered = filterByDateRange(filtered, filters.activityFrom, filters.activityTo)
    
    if (filters.status) {
      const normalizedStatus = String(filters.status).trim().toLowerCase()
      filtered = filtered.filter((cert: any) => String(cert?.status ?? '').trim().toLowerCase() === normalizedStatus)
    }
    
    if (filters.type) {
      const normalizedType = String(filters.type).trim().toLowerCase()
      filtered = filtered.filter((cert: any) => String(cert?.type ?? '').trim().toLowerCase() === normalizedType)
    }


    // VERA-315: multi-select status-family filter — union of every selected family's
    // underlying raw statuses. Empty/undefined `statuses` means no status filtering (show all).
    if (filters.statuses?.length) {
      const allowedStatuses = new Set(
        filters.statuses.flatMap(family => STATUS_FAMILY_MAP[family] ?? [family])
      )
      filtered = filtered.filter((cert: any) =>
        allowedStatuses.has(String(cert?.status ?? '').trim().toLowerCase())
      )
    }

    return filtered
  }, [certificates, filters.certificateIds, filters.activityFrom, filters.activityTo, filters.status, filters.type, filters.statuses])

  const sortedCertificates = useMemo(() => {
    const descriptors: SortDescriptor[] = []
    
    if (filters.sort_by && filters.sort) {
      descriptors.push({ field: filters.sort_by, direction: filters.sort })
    }
    
    if (additionalSorts.length) {
      descriptors.push(...additionalSorts)
    }
    
    return sortCertificates(baseFilteredCertificates, descriptors)
  }, [baseFilteredCertificates, filters.sort_by, filters.sort, additionalSorts])

  const visibleCertificates = useMemo(() => {
    if (tab === 'all') return sortedCertificates
    
    const targetStatus = String(tab).trim().toLowerCase()
    return sortedCertificates.filter((cert: any) => 
      String(cert?.status ?? '').trim().toLowerCase() === targetStatus
    )
  }, [sortedCertificates, tab])

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.certificateIds ||
      filters.activityFrom ||
      filters.activityTo ||
      filters.status ||
      filters.type ||
      filters.query ||
      filters.statuses?.length
    )
  }, [filters.certificateIds, filters.activityFrom, filters.activityTo, filters.status, filters.type, filters.query, filters.statuses])

  return {
    baseFilteredCertificates,
    sortedCertificates,
    visibleCertificates,
    hasActiveFilters,
  }
}
